/**
 * Flow chart serialization.
 *
 * A `.flow` file is a ZIP bundle (see flowBundle.ts) with:
 *   - beatmap.meta.bin   — the chart JSON (FlowChartFile, this module)
 *   - cover.*            — optional cover image
 *   - <song>.<mp3|ogg|wav|…>  — the audio file
 *
 * Older `.flow` files on disk that are plain JSON keep loading via the
 * fallback path in `unpackBundle()`.
 *
 * The on-disk JSON itself is a small, versioned envelope: chart data +
 * tiny metadata header.
 */

import type { FlowNote, FlowTrailWaypoint, FlowDifficultyName, FlowLaneCount } from './types';
import { FLOW_LANE_COUNT, FLOW_DIFFICULTIES, laneCountFor, DEFAULT_FLOW_LANE_COUNT, migrateFlowDifficultyName } from './types';
import { useFlowStore } from './flowStore';
import {
  packBundle,
  unpackBundle,
  extensionFromFilename,
  AUDIO_EXT_FROM_MIME,
  AUDIO_MIME_FROM_EXT,
  type BundleContents,
} from './flowBundle';
import { readCachedAudioBytes } from './audioCache';
import { API_BASE } from '../../api/apiBase';

/**
 * Top-level shape of a serialized Flow chart file (the inner JSON).
 *
 * The schema matches the game-engine .meta format: song metadata at the
 * top, then a `difficulties` array each containing `taps`, `rails`,
 * `flicks`, and `waypoints`. We currently emit a single difficulty.
 *
 * The on-disk JSON is consumed by the game runtime as well as by us — so
 * lane indices are converted to a continuous `Position` in [0,1] where
 * each of N lanes gets the centre of its slice: lane i → (i + 0.5) / N.
 */
export interface FlowChartFile {
  fullName: string;
  id: number;
  /** Unix timestamp (seconds). */
  updateDate: number;
  name: string;
  author: string;
  bpm: number;
  audioDuration: number;
  audioSamples: number;
  audioFrequency: number;
  audioChannels: number;
  startOffset: number;
  difficulties: FlowChartDifficulty[];
  /** Editor-only timeline bookmarks (beats). The game runtime ignores unknown
   *  keys, and older .flow files simply lack it. */
  bookmarks?: { time_beats: number; label?: string }[];
}

export interface FlowChartDifficulty {
  name: string;
  /** Numeric difficulty (0-indexed): Easy=0, Medium=1, Hard=2, Expert=3.
   *  The engine also reserves 4 for Master, which this editor doesn't author. */
  difficulty: number;
  /** Lane count for this difficulty (3 or 4). Absent ⇒ 4 (older charts). The
   *  taps/rails/flicks Position fields are normalized against THIS count. */
  laneCount?: number;
  taps: FlowChartTap[];
  rails: FlowChartRail[];
  flicks: FlowChartFlick[];
  waypoints: any[];
}

export interface FlowChartTap {
  legacyType: 0;
  Type: 0;
  Id: string;
  Beat: number;
  Time: number;
  Position: number;
}

export interface FlowChartFlick {
  Type: 3;
  /** NoteDirection enum: None=0, Left=1, Right=2, Up=3, Down=4 */
  Direction: 0 | 1 | 2 | 3 | 4;
  KeyboardBehaviour: 0;
  Id: string;
  Beat: number;
  Time: number;
  Position: number;
}

export interface FlowChartRailWaypoint {
  Type: 2;
  Direction: 0 | 1 | 2;
  Id: string;
  Beat: number;
  Time: number;
  Position: number;
}

export interface FlowChartRail {
  EndTime: number;
  Duration: number;
  EndPosition: number;
  Waypoints: FlowChartRailWaypoint[];
  Type: 1;
  Id: string;
  Beat: number;
  Time: number;
  Position: number;
}

/** Resolution of the `Beat` integer relative to a musical beat. The sample
 *  data uses 64 ticks per beat (Beat 896 ↔ musical beat 14 at 142 BPM). */
const TICKS_PER_BEAT = 64;

/** Convert a lane index to the centre of its [0,1] slice. Default stays the
 *  LEGACY 4-lane count (FLOW_LANE_COUNT is now the 5-lane MAX) — the legacy
 *  synapse-flow-chart paths below rely on the omitted-arg behavior. */
function laneToPosition(lane: number, laneCount: number = DEFAULT_FLOW_LANE_COUNT): number {
  return (lane + 0.5) / laneCount;
}

/** Convert a normalized position back to the nearest lane index. */
function positionToLane(pos: number, laneCount: number = DEFAULT_FLOW_LANE_COUNT): number {
  const lane = Math.floor(pos * laneCount);
  return Math.max(0, Math.min(laneCount - 1, lane));
}

/** Direction code (NoteDirection enum) for one of our directional notes. */
function flickDirection(type: FlowNote['type']): 0 | 1 | 2 | 3 {
  if (type === 'arrow-left') return 1;
  if (type === 'arrow-right') return 2;
  if (type === 'arrow-center') return 3;
  return 0;
}

/** Inverse: NoteDirection → our internal note type (arrow-* / tap). */
function flickTypeFromDirection(dir: number): FlowNote['type'] {
  if (dir === 1) return 'arrow-left';
  if (dir === 2) return 'arrow-right';
  if (dir === 3) return 'arrow-center';
  return 'tap';
}

/**
 * Game-engine difficulty enum (0-indexed):
 *   0 = Easy
 *   1 = Medium
 *   2 = Hard
 *   3 = Expert
 *   4 = Master  (not exposed in this editor)
 */
// Name → numeric difficulty for the .flow format. 'Medium' kept for back-compat
// (legacy charts). Custom-named difficulties fall back to 2 (Hard) for the
// numeric field; their real name is preserved in the `name` field.
const DIFFICULTY_TO_NUMBER: Record<string, number> = {
  Easy: 0, Normal: 1, Medium: 1, Hard: 2, Expert: 3, Master: 4,
};
function difficultyToNumber(name: string): number {
  return DIFFICULTY_TO_NUMBER[name] ?? 2;
}

function difficultyFromNumber(n: number): string {
  if (n === 0) return 'Easy';
  if (n === 1) return 'Normal';
  if (n === 3) return 'Expert';
  if (n === 4) return 'Master';
  // 2 → Hard, and anything else falls back to Hard.
  return 'Hard';
}

// ── Serialize ────────────────────────────────────────────────────────────────

/**
 * Build a FlowChartFile from the current store state. Pure — does no I/O.
 * Produces the game-engine schema (top-level metadata + difficulties[*]
 * with taps/rails/flicks).
 *
 * The current chart contributes a single difficulty entry. Future support
 * for multiple difficulties (per-chart variants) would push more entries
 * into the `difficulties` array.
 */
export function buildChartFile(): FlowChartFile {
  const s = useFlowStore.getState();
  const bpm = s.chart.bpm;
  const secondsPerBeat = 60 / Math.max(bpm, 1);

  // Assemble the per-difficulty buckets. The active diff's live notes live
  // in `chart.notes`; everything else is in `chart.difficultyNotes`. We
  // serialize all four so the on-disk file is complete regardless of which
  // diff happened to be open when the user hit Save.
  const notesByDiff: Record<FlowDifficultyName, FlowNote[]> = {
    Easy: [], Medium: [], Hard: [], Expert: [],
  };
  notesByDiff[s.activeDifficulty] = s.chart.notes;
  const stored = s.chart.difficultyNotes ?? {};
  for (const name of FLOW_DIFFICULTIES) {
    if (name === s.activeDifficulty) continue;
    notesByDiff[name] = stored[name] ?? [];
  }

  const difficulties: FlowChartDifficulty[] = FLOW_DIFFICULTIES.map(name =>
    serializeDifficulty(name, notesByDiff[name], secondsPerBeat, laneCountFor(s.chart, name)),
  );

  const songName = s.songName.trim();
  const songAuthor = s.songAuthor.trim();
  const fullName = buildFullName(songName, songAuthor);

  return {
    fullName,
    id: 0,
    updateDate: Math.floor(Date.now() / 1000),
    name: songName,
    author: songAuthor,
    bpm,
    audioDuration: s.duration,
    audioSamples: s.audioSamples,
    audioFrequency: s.audioFrequency,
    audioChannels: s.audioChannels,
    startOffset: s.chart.offset_seconds,
    difficulties,
    ...(s.chart.bookmarks?.length ? { bookmarks: s.chart.bookmarks } : {}),
  };
}

/** Serialize one difficulty's notes into the on-disk taps/rails/flicks shape.
 *  Pure — no store reads. Caller owns picking the right note array per diff. */
function serializeDifficulty(
  name: FlowDifficultyName,
  diffNotes: FlowNote[],
  secondsPerBeat: number,
  laneCount: number = DEFAULT_FLOW_LANE_COUNT,
): FlowChartDifficulty {
  const taps: FlowChartTap[] = [];
  const rails: FlowChartRail[] = [];
  const flicks: FlowChartFlick[] = [];

  for (const n of diffNotes) {
    const beatTicks = Math.round(n.time_beats * TICKS_PER_BEAT);
    const timeSec = n.time_seconds || n.time_beats * secondsPerBeat;
    const position = laneToPosition(n.lane, laneCount);

    // Trail (multi-segment OR single-lane legacy with duration_beats only).
    // Internal model: if `waypoints` has ≥ 2 entries, use them as-is. Else
    // synthesize a two-waypoint list from (head, head+duration) on the
    // same lane so legacy single-lane trails keep serializing the same.
    const wps: FlowTrailWaypoint[] | null = (n.waypoints && n.waypoints.length >= 2)
      ? n.waypoints
      : (n.duration_beats && n.duration_beats > 0
          ? [
              { lane: n.lane, time_beats: n.time_beats },
              { lane: n.lane, time_beats: n.time_beats + n.duration_beats },
            ]
          : null);

    if (wps) {
      const headTicks = Math.round(wps[0].time_beats * TICKS_PER_BEAT);
      const headTime = wps[0].time_beats * secondsPerBeat;
      const tailTime = wps[wps.length - 1].time_beats * secondsPerBeat;
      const tailPosition = laneToPosition(wps[wps.length - 1].lane, laneCount);

      const Waypoints = wps.map((w, i) => {
        const pos = laneToPosition(w.lane, laneCount);
        const beat = Math.round(w.time_beats * TICKS_PER_BEAT);
        const t = w.time_beats * secondsPerBeat;
        let direction: 0 | 1 | 2 = 0;
        if (i > 0 && i < wps.length - 1) {
          const prev = laneToPosition(wps[i - 1].lane, laneCount);
          if (pos < prev) direction = 1;
          else if (pos > prev) direction = 2;
        }
        return {
          Type: 2 as const,
          Direction: direction,
          Id: `${headTicks}_rail_${i}`,
          Beat: beat, Time: t, Position: pos,
        };
      });

      rails.push({
        EndTime: tailTime,
        Duration: tailTime - headTime,
        EndPosition: tailPosition,
        Waypoints,
        Type: 1,
        Id: `rail_${headTicks}_${n.lane % 2}`,
        Beat: headTicks, Time: headTime, Position: laneToPosition(wps[0].lane, laneCount),
      });
      continue;
    }

    if (n.type === 'arrow-left' || n.type === 'arrow-right' || n.type === 'arrow-center') {
      flicks.push({
        Type: 3,
        Direction: flickDirection(n.type),
        KeyboardBehaviour: 0,
        Id: `flick_${beatTicks}`,
        Beat: beatTicks,
        Time: timeSec,
        Position: position,
      });
      continue;
    }

    taps.push({
      legacyType: 0,
      Type: 0,
      Id: `${beatTicks}`,
      Beat: beatTicks,
      Time: timeSec,
      Position: position,
    });
  }

  taps.sort((a, b) => a.Beat - b.Beat);
  rails.sort((a, b) => a.Beat - b.Beat);
  flicks.sort((a, b) => a.Beat - b.Beat);

  return {
    name,
    difficulty: difficultyToNumber(name),
    laneCount,
    taps,
    rails,
    flicks,
    waypoints: [],
  };
}

/** "MORE — K/DA (League of Legends)" → "0000-MORE-KDA(LeagueofLegends)" */
function buildFullName(name: string, author: string): string {
  const slug = (s: string) => s.replace(/[^\w]/g, '');
  const left = slug(name) || 'Untitled';
  const right = slug(author);
  return `0000-${left}${right ? `-${right}` : ''}`;
}

/**
 * Assemble the .flow zip bundle for the current chart. Reads the cover
 * out of the store and the audio bytes out of IndexedDB. Returns the
 * Blob (zip) plus a suggested filename.
 */
async function buildBundleBlob(
  nameOverride?: string,
  visualizers: { name: string; json: string }[] = [],
): Promise<{ blob: Blob; filename: string; chartFile: FlowChartFile }> {
  const file = buildChartFile();
  const chartJson = JSON.stringify(file, null, 2);

  const s = useFlowStore.getState();

  // Cover comes from the store (held in memory after the user picks it).
  const cover = s.coverBytes && s.coverName
    ? { bytes: s.coverBytes, filename: s.coverName }
    : null;

  // Audio bytes live in IndexedDB (audioCache) so a refresh keeps them.
  // We read the bytes back rather than fetching the blob URL, because the
  // blob URL might have already been revoked by an earlier setAudio call.
  let audio: { bytes: ArrayBuffer; filename: string } | null = null;
  const cached = await readCachedAudioBytes();
  if (cached) {
    const ext =
      extensionFromFilename(cached.name) ||
      AUDIO_EXT_FROM_MIME[cached.type] ||
      'mp3';
    const base = cached.name.replace(/\.[^.]+$/, '') || 'audio';
    const safeBase = base.replace(/[\\/:*?"<>|]+/g, '_') || 'audio';
    audio = { bytes: cached.bytes, filename: `${safeBase}.${ext}` };
  } else if (s.audioUrl && s.audioName) {
    // Fallback: pull bytes directly from the blob URL if no cache entry
    // (shouldn't happen in normal use — audioLoader caches on load).
    try {
      const blob = await fetch(s.audioUrl).then(r => r.blob());
      const bytes = await blob.arrayBuffer();
      audio = { bytes, filename: s.audioName };
    } catch { /* skip — chart-only bundle */ }
  }

  const blob = await packBundle({ chartJson, cover, audio, visualizers });

  // Filename priority: explicit override → song name from the meta panel →
  // audio filename (stripped of its extension) → "untitled".
  const s2 = useFlowStore.getState();
  const audioBase = s2.audioName ? s2.audioName.replace(/\.[^.]+$/, '') : '';
  const baseRaw = nameOverride
    || file.name
    || audioBase
    || 'untitled';
  const base = baseRaw.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'untitled';
  return { blob, filename: `${base}.flow`, chartFile: file };
}

/**
 * Trigger a browser download of the current chart as a .flow bundle.
 * Returns the suggested filename so the caller can flash a toast.
 */
export async function downloadCurrentChart(
  visualizers: { name: string; json: string }[] = [],
): Promise<string> {
  const { blob, filename } = await buildBundleBlob(undefined, visualizers);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}

/**
 * Build the current chart's `.flow` bundle (zip blob + suggested filename)
 * without downloading or uploading it. Used by the local-folder save path
 * (File System Access API) to write the bytes straight to disk.
 */
export async function buildFlowBundle(
  nameOverride?: string,
): Promise<{ blob: Blob; filename: string }> {
  const { blob, filename } = await buildBundleBlob(nameOverride);
  return { blob, filename };
}

// ── Server-side library (beatmaps/flow on the backend disk) ────────────────


/**
 * Persist the current chart as a .flow zip bundle on the server. The
 * whole bundle (chart + cover + audio) is uploaded as a single binary
 * body so the server doesn't have to know anything about the internal
 * layout — it just receives bytes and writes them to disk.
 *
 * If `nameOverride` is given, it's used as the bare filename (no
 * extension); otherwise the audio name is used, falling back to
 * "untitled".
 */
export async function saveChartToServer(
  nameOverride?: string,
): Promise<{ name: string; path: string }> {
  const { blob, filename } = await buildBundleBlob(nameOverride);
  // Strip the `.flow` extension — the backend re-appends it via its
  // own slug helper.
  const bareName = filename.replace(/\.flow$/i, '');
  const res = await fetch(
    `${API_BASE}/api/flow/charts?name=${encodeURIComponent(bareName)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/zip' },
      body: blob,
    },
  );
  if (!res.ok) {
    let msg: string;
    try { msg = (await res.json()).error || `HTTP ${res.status}`; }
    catch { msg = await res.text().catch(() => `HTTP ${res.status}`); }
    throw new Error(`Save to server failed: ${msg}`);
  }
  return res.json() as Promise<{ name: string; path: string }>;
}

export interface ServerChartEntry {
  name: string;
  size: number;
  modified: number;
  owner?: string;
  visibility?: 'private' | 'public';
  is_owner?: boolean;
}

/** List the .flow files currently in the server library. */
export async function listServerCharts(): Promise<ServerChartEntry[]> {
  const res = await fetch(`${API_BASE}/api/flow/charts`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.charts) ? json.charts : [];
}

/**
 * Fetch a chart bundle from the server, unpack it, and apply chart +
 * cover + audio to the store.
 */
export async function loadServerChart(name: string): Promise<FlowChartFile> {
  const res = await fetch(`${API_BASE}/api/flow/charts/${encodeURIComponent(name)}`);
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new FlowChartLoadError(text);
  }
  const buffer = await res.arrayBuffer();
  return applyBundle(buffer);
}

/** Delete a chart from the server library. */
export async function deleteServerChart(name: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/flow/charts/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text);
  }
}

/** Publish/unpublish a server chart (owner-only; the backend 403s otherwise). */
export async function setServerChartVisibility(
  name: string,
  isPublic: boolean,
): Promise<{ success: boolean; name: string; visibility: 'private' | 'public' }> {
  const res = await fetch(
    `${API_BASE}/api/flow/charts/${encodeURIComponent(name)}/visibility`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ public: isPublic }),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text);
  }
  return res.json();
}

/**
 * Ask the (local) backend to reveal the beatmaps/flow folder in the OS file
 * explorer. Returns the resolved path. Throws on HTTP error.
 */
export async function openServerChartsFolder(): Promise<string> {
  const res = await fetch(`${API_BASE}/api/flow/open-folder`, { method: 'POST' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json?.path ?? '';
}

// ── Deserialize ──────────────────────────────────────────────────────────────

export class FlowChartLoadError extends Error {}

/**
 * Read a File picked from disk and apply it to the store. Works with
 * both new .flow zip bundles and legacy plain-JSON .flow files.
 */
export async function loadChartFromFile(file: File): Promise<FlowChartFile> {
  const buffer = await file.arrayBuffer();
  return applyBundle(buffer);
}

/**
 * Unpack a bundle (or legacy JSON) buffer and push its pieces into the
 * store: chart → setChart, cover → setCover, audio → setAudio.
 */
async function applyBundle(buffer: ArrayBuffer): Promise<FlowChartFile> {
  let contents: BundleContents;
  try {
    contents = await unpackBundle(buffer);
  } catch (err: any) {
    throw new FlowChartLoadError(err?.message || 'Could not unpack .flow bundle');
  }

  // Chart JSON
  let parsed: any;
  try {
    parsed = JSON.parse(contents.chartJson);
  } catch (err: any) {
    throw new FlowChartLoadError(`Not a valid JSON file: ${err.message}`);
  }
  const loaded = loadChartFromObject(parsed);

  const store = useFlowStore.getState();

  // Cover — write to store with a fresh blob URL.
  if (contents.cover) {
    const blob = new Blob([contents.cover.bytes], { type: contents.cover.mime });
    store.setCover({
      url: URL.createObjectURL(blob),
      name: contents.cover.filename,
      mime: contents.cover.mime,
      bytes: contents.cover.bytes,
    });
  } else {
    store.clearCover();
  }

  // Audio — same flow as the audio loader: blob URL → setAudio, bytes →
  // IndexedDB cache so refreshes round-trip.
  if (contents.audio) {
    const blob = new Blob([contents.audio.bytes], { type: contents.audio.mime });
    const url = URL.createObjectURL(blob);
    store.setAudio({
      url,
      name: contents.audio.filename,
      bpm: loaded.bpm,
      offsetSeconds: loaded.startOffset,
      durationSeconds: loaded.audioDuration,
      audioFrequency: loaded.audioFrequency,
      audioChannels: loaded.audioChannels,
      audioSamples: loaded.audioSamples,
    });
    // Cache the bytes for next session via IndexedDB. We import lazily
    // to keep this module's dependency surface flat.
    void (async () => {
      try {
        const { cacheAudioFile } = await import('./audioCache');
        const audioFile = new File([blob], contents.audio!.filename, {
          type: contents.audio!.mime,
        });
        await cacheAudioFile(audioFile);
      } catch { /* non-fatal */ }
    })();
  }

  return loaded;
}

/**
 * Apply an already-parsed object to the store (used by drag-drop too).
 *
 * Handles both the new game-engine schema (top-level metadata +
 * `difficulties[*].taps/rails/flicks`) and the legacy in-house schema
 * (`{format:'synapse-flow-chart', chart:{bpm, offset_seconds, notes}}`)
 * so old `.flow` files on disk keep loading.
 */
export function loadChartFromObject(raw: unknown): FlowChartFile {
  const file = validate(raw);
  const store = useFlowStore.getState();
  const bpm = file.bpm;
  const secondsPerBeat = 60 / Math.max(bpm, 1);

  // Parse every difficulty in the file. Each one yields its own FlowNote[]
  // so the editor can switch between them without losing work.
  const notesByDiff: Partial<Record<FlowDifficultyName, FlowNote[]>> = {};
  const laneCounts: Partial<Record<FlowDifficultyName, FlowLaneCount>> = {};
  for (const d of file.difficulties) {
    const lc: FlowLaneCount = d.laneCount === 3 ? 3 : d.laneCount === 5 ? 5 : DEFAULT_FLOW_LANE_COUNT;
    laneCounts[d.name] = lc;
    notesByDiff[d.name] = difficultyToFlowNotes(d, secondsPerBeat, lc);
  }

  // The active diff lands in chart.notes; everything else goes into the
  // snapshot map. Default to Hard if the file's first diff isn't present
  // (e.g. some legacy single-diff dumps).
  const firstDiff = file.difficulties[0];
  const activeName: FlowDifficultyName = firstDiff?.name ?? 'Hard';
  const activeNotes = notesByDiff[activeName] ?? [];
  const difficultyNotes: Partial<Record<FlowDifficultyName, FlowNote[]>> = {};
  for (const name of FLOW_DIFFICULTIES) {
    if (name === activeName) continue;
    difficultyNotes[name] = notesByDiff[name] ?? [];
  }

  store.setChart({
    bpm,
    offset_seconds: file.startOffset,
    notes: activeNotes,
    difficultyNotes,
    laneCounts,
    meta: {
      title: file.name,
      artist: file.author,
    },
    ...(Array.isArray(file.bookmarks) && file.bookmarks.length
      ? { bookmarks: file.bookmarks.filter(b => typeof b?.time_beats === 'number') }
      : {}),
  });
  store.setDuration(file.audioDuration);
  // Use the non-swapping setter: setChart above already put activeName's
  // notes into chart.notes and parked the rest in difficultyNotes.
  store.setActiveDifficultyAfterLoad(activeName);
  store.setSongName(file.name);
  store.setSongAuthor(file.author);
  store.setCurrentTime(0);
  store.clearSelection();

  return file;
}

/** Convert one on-disk difficulty's taps/rails/flicks into our internal
 *  FlowNote array. Pure helper — no store side effects. */
function difficultyToFlowNotes(diff: FlowChartDifficulty, secondsPerBeat: number, laneCount: number = DEFAULT_FLOW_LANE_COUNT): FlowNote[] {
  const notes: FlowNote[] = [];

  for (const t of diff.taps) {
    notes.push({
      id: `t-${diff.name}-${t.Id || t.Beat}`,
      lane: positionToLane(t.Position, laneCount) as FlowNote['lane'],
      time_beats: t.Beat / TICKS_PER_BEAT,
      time_seconds: typeof t.Time === 'number' ? t.Time : (t.Beat / TICKS_PER_BEAT) * secondsPerBeat,
      type: 'tap',
      duration_beats: 0,
    });
  }
  for (const f of diff.flicks) {
    notes.push({
      id: `f-${diff.name}-${f.Id || f.Beat}`,
      lane: positionToLane(f.Position, laneCount) as FlowNote['lane'],
      time_beats: f.Beat / TICKS_PER_BEAT,
      time_seconds: typeof f.Time === 'number' ? f.Time : (f.Beat / TICKS_PER_BEAT) * secondsPerBeat,
      type: flickTypeFromDirection(f.Direction),
      duration_beats: 0,
    });
  }
  for (const r of diff.rails) {
    const headBeat = r.Beat;
    const headBeats = headBeat / TICKS_PER_BEAT;
    const fileWps = Array.isArray(r.Waypoints) && r.Waypoints.length > 0
      ? r.Waypoints
      : [{ Beat: headBeat, Position: r.Position, Time: r.Time, Type: 2 as const, Direction: 0 as const, Id: '' }];
    const internalWps: FlowTrailWaypoint[] = fileWps.map(w => ({
      lane: positionToLane(w.Position, laneCount) as FlowNote['lane'],
      time_beats: w.Beat / TICKS_PER_BEAT,
    }));
    const tailBeats = internalWps[internalWps.length - 1].time_beats;
    notes.push({
      id: `r-${diff.name}-${r.Id || r.Beat}`,
      lane: internalWps[0].lane,
      time_beats: headBeats,
      time_seconds: typeof r.Time === 'number' ? r.Time : headBeats * secondsPerBeat,
      type: 'tap',
      duration_beats: Math.max(0, tailBeats - headBeats),
      waypoints: internalWps.length >= 2 ? internalWps : undefined,
    });
  }

  notes.sort((a, b) => a.time_beats - b.time_beats);
  return notes;
}

// ── Validation ───────────────────────────────────────────────────────────────

function validate(raw: any): FlowChartFile {
  if (!raw || typeof raw !== 'object') {
    throw new FlowChartLoadError('Expected a JSON object at the top level');
  }

  // Legacy file (format tag + nested chart). Migrate forward to the new
  // schema so the rest of the pipeline only ever sees one shape.
  if (raw.format === 'synapse-flow-chart') {
    return migrateLegacy(raw);
  }

  if (!Array.isArray(raw.difficulties) || raw.difficulties.length === 0) {
    throw new FlowChartLoadError('Missing `difficulties` array');
  }
  if (typeof raw.bpm !== 'number' || raw.bpm <= 0) {
    throw new FlowChartLoadError('`bpm` must be a positive number');
  }

  const difficulties: FlowChartDifficulty[] = raw.difficulties.map((d: any) => ({
    name: typeof d.name === 'string' && d.name.trim()
      ? migrateFlowDifficultyName(d.name)
      : difficultyFromNumber(Number(d.difficulty) || 3),
    difficulty: typeof d.difficulty === 'number' ? d.difficulty : 3,
    taps: Array.isArray(d.taps) ? d.taps : [],
    rails: Array.isArray(d.rails) ? d.rails : [],
    flicks: Array.isArray(d.flicks) ? d.flicks : [],
    waypoints: Array.isArray(d.waypoints) ? d.waypoints : [],
  }));

  return {
    fullName: typeof raw.fullName === 'string' ? raw.fullName : '',
    id: typeof raw.id === 'number' ? raw.id : 0,
    updateDate: typeof raw.updateDate === 'number' ? raw.updateDate : Math.floor(Date.now() / 1000),
    name: typeof raw.name === 'string' ? raw.name : '',
    author: typeof raw.author === 'string' ? raw.author : '',
    bpm: raw.bpm,
    audioDuration: typeof raw.audioDuration === 'number' ? raw.audioDuration : 0,
    audioSamples: typeof raw.audioSamples === 'number' ? raw.audioSamples : 0,
    audioFrequency: typeof raw.audioFrequency === 'number' ? raw.audioFrequency : 44100,
    audioChannels: typeof raw.audioChannels === 'number' ? raw.audioChannels : 2,
    startOffset: typeof raw.startOffset === 'number' ? raw.startOffset : 0,
    difficulties,
  };
}

/**
 * Convert a legacy `{format:'synapse-flow-chart', chart:{...notes}}` file
 * into the new schema. The notes carry lane/time/type/duration we can
 * re-emit as taps/flicks/rails.
 */
function migrateLegacy(raw: any): FlowChartFile {
  const c = raw.chart || {};
  const bpm = typeof c.bpm === 'number' && c.bpm > 0 ? c.bpm : 120;
  const offset = typeof c.offset_seconds === 'number' ? c.offset_seconds : 0;
  const duration = typeof raw.duration_seconds === 'number' ? raw.duration_seconds : 0;
  const difficultyName: FlowChartDifficulty['name'] =
    typeof raw.difficulty === 'string' && raw.difficulty.trim()
      ? migrateFlowDifficultyName(raw.difficulty)
      : 'Hard';

  const taps: FlowChartTap[] = [];
  const rails: FlowChartRail[] = [];
  const flicks: FlowChartFlick[] = [];
  const secondsPerBeat = 60 / bpm;

  for (const n of Array.isArray(c.notes) ? c.notes : []) {
    if (!n || typeof n !== 'object') continue;
    if (typeof n.time_beats !== 'number' || n.time_beats < 0) continue;
    if (typeof n.lane !== 'number' || n.lane < 0 || n.lane >= FLOW_LANE_COUNT) continue;
    const allowedTypes = ['tap', 'arrow-left', 'arrow-center', 'arrow-right'] as const;
    let type: FlowNote['type'] = 'tap';
    if (allowedTypes.includes(n.type)) type = n.type;
    // Legacy strum/hopo migrate to tap
    if (n.type === 'strum' || n.type === 'hopo') type = 'tap';

    const beatTicks = Math.round(n.time_beats * TICKS_PER_BEAT);
    const t = typeof n.time_seconds === 'number' ? n.time_seconds : n.time_beats * secondsPerBeat;
    const pos = laneToPosition(n.lane);

    if ((n.duration_beats && n.duration_beats > 0) || (Array.isArray(n.waypoints) && n.waypoints.length >= 2)) {
      const wps: FlowTrailWaypoint[] = (Array.isArray(n.waypoints) && n.waypoints.length >= 2)
        ? n.waypoints
        : [
            { lane: n.lane, time_beats: n.time_beats },
            { lane: n.lane, time_beats: n.time_beats + (n.duration_beats || 0) },
          ];
      const headTicks = Math.round(wps[0].time_beats * TICKS_PER_BEAT);
      const tailT = wps[wps.length - 1].time_beats * secondsPerBeat;
      const Waypoints = wps.map((w, i) => {
        const wpPos = laneToPosition(w.lane);
        const wpBeat = Math.round(w.time_beats * TICKS_PER_BEAT);
        const wpT = w.time_beats * secondsPerBeat;
        let direction: 0 | 1 | 2 = 0;
        if (i > 0 && i < wps.length - 1) {
          const prev = laneToPosition(wps[i - 1].lane);
          if (wpPos < prev) direction = 1;
          else if (wpPos > prev) direction = 2;
        }
        return {
          Type: 2 as const, Direction: direction,
          Id: `${headTicks}_rail_${i}`,
          Beat: wpBeat, Time: wpT, Position: wpPos,
        };
      });
      rails.push({
        EndTime: tailT, Duration: tailT - t,
        EndPosition: laneToPosition(wps[wps.length - 1].lane),
        Waypoints,
        Type: 1, Id: `rail_${headTicks}_${n.lane % 2}`,
        Beat: headTicks, Time: t, Position: laneToPosition(wps[0].lane),
      });
      continue;
    }
    if (type !== 'tap') {
      flicks.push({
        Type: 3, Direction: flickDirection(type), KeyboardBehaviour: 0,
        Id: `flick_${beatTicks}`, Beat: beatTicks, Time: t, Position: pos,
      });
      continue;
    }
    taps.push({
      legacyType: 0, Type: 0, Id: `${beatTicks}`, Beat: beatTicks, Time: t, Position: pos,
    });
  }

  return {
    fullName: '',
    id: 0,
    updateDate: Math.floor(Date.now() / 1000),
    name: c?.meta?.title || '',
    author: c?.meta?.artist || '',
    bpm,
    audioDuration: duration,
    audioSamples: 0,
    audioFrequency: 44100,
    audioChannels: 2,
    startOffset: offset,
    difficulties: [
      {
        name: difficultyName,
        difficulty: difficultyToNumber(difficultyName),
        taps, rails, flicks, waypoints: [],
      },
    ],
  };
}

// Re-export the audio MIME map so other modules don't need a second
// source of truth.
export { AUDIO_MIME_FROM_EXT };
